import React from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useTableRow, { TableRowProps } from 'shared/components/table/components/table-row/hooks';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof, ResolveByNestedKey } from 'shared/components/table/utils/nested-keyof-utils';

const TableRow = <DataType extends object, Key extends NestedKeyof<DataType>>(
  props: Readonly<TableRowProps<DataType, Key>>,
): React.JSX.Element => {
  const { columns, row, rowIndex, clickedRow, currentItem, rowsAreSelectable, overflowMenuOptions, onMoreClick, disabled } = props;
  const { handleRowClick, toggleRowSelection, getCursorStyling, currentCellProps, isChecked, rowErrorMessage } = useTableRow(props);

  const renderColumns = (row: RowItem<DataType>, rowIndex: number): React.JSX.Element => (
    <>
      {columns.map((column, columnIndex) => (
        <TableCell
          className={`table-cell ${'isHighlighted' in row && row.isHighlighted ? 'table-cell-highlighted' : ''}`}
          key={`table-cell-${rowIndex}-${columnIndex}`}
          data-testid={`table-cell-${rowIndex}-${columnIndex}`}
          {...currentCellProps(column, row)}
        >
          {column.renderCustomContentProvider
            ? column.renderCustomContentProvider(row.data, rowIndex)
            : (ResolveByNestedKey<DataType>(column.key, row.data) as string)}
        </TableCell>
      ))}
    </>
  );

  return (
    <>
      <MuiTableRow
        data-testid={`table-row-${rowIndex}`}
        className={`${row.showColorOnHover === false ? 'no-hover-table-row' : ''}
        ${row.isHighlighted ? 'table-row-highlighted' : 'table-row'}
        ${row.isFaded ? 'table-row-faded' : ''}`}
        selected={row.data === currentItem}
        onClick={disabled ? undefined : () => handleRowClick(row)}
        style={getCursorStyling(row)}
      >
        {rowsAreSelectable && (
          <TableCell padding="checkbox" className="table-cell">
            <Checkbox
              data-testid={`selection-checkbox-${rowIndex}`}
              className={`selection-checkbox ${isChecked ? 'is-checked' : ''}`}
              color="primary"
              checked={isChecked}
              onChange={(_, checked) => toggleRowSelection(checked, row)}
              disabled={disabled}
              slotProps={
                {
                  input: {
                    'data-testid': `checkbox-cell-for-row-${rowIndex}`,
                  },
                } as CheckboxProps['slotProps']
              }
            />
          </TableCell>
        )}
        {renderColumns(row, rowIndex)}
        {overflowMenuOptions && !row.hideOverflowMenuOptions && (
          <TableCell
            align="right"
            width="24px"
            className={`table-cell ${'isHighlighted' in row && row.isHighlighted ? 'table-cell-highlighted' : ''}`}
          >
            <IconButton
              data-testid={`more-icon-button-${rowIndex}`}
              className={`more-icon-button ${clickedRow === row.data ? 'is-selected' : ''}`}
              onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onMoreClick(event, row)}
            >
              <MoreHorizIcon className="more-icon" />
            </IconButton>
          </TableCell>
        )}
      </MuiTableRow>
      {rowErrorMessage && (
        <MuiTableRow className="no-hover-table-row">
          {rowsAreSelectable && <TableCell />}
          <TableCell
            colSpan={(() => {
              const baseColumnCount = columns.length;
              const overflowColumnCount = overflowMenuOptions ? 1 : 0;
              return rowsAreSelectable ? baseColumnCount : baseColumnCount + overflowColumnCount;
            })()}
          >
            <Typography variant="subtitle1" color="error" className="error-message">
              {rowErrorMessage}
            </Typography>
          </TableCell>
        </MuiTableRow>
      )}
    </>
  );
};

export default TableRow;
